import { isEntityKycCustomer } from "../../../shared/kycAuthentication";
import { useState } from "react";
import { Button, Modal, TextInput, Group, Stack, Text } from "@mantine/core";
import { useDisclosure } from "@mantine/hooks";
import { notifications } from "@mantine/notifications";
import { httpsCallable } from "firebase/functions";
import { getAuth } from "firebase/auth";
import type { CreateContractLink, BaseResult, Customer } from "../types";
import { useFunctions } from "../helpers/firebaseContext";

// Kuwait mobile: exactly 8 digits, starts with 4, 5, 6 or 9. No country code (backend adds it).
const isValidKuwaitMobile = (value: string): boolean => /^[4569]\d{7}$/.test(value);

// Cleans typed/pasted input: "+965 5123 4567" / "00965 51234567" → "51234567"
const cleanInput = (raw: string): string => {
    let digits = raw.replace(/\D/g, "");
    if (digits.startsWith("00965")) digits = digits.slice(5);
    else if (digits.startsWith("965") && digits.length > 8) digits = digits.slice(3);
    return digits.slice(0, 8);
};

interface Props {
    customer: Customer;
}

export function CompanySendKycLinkButton({ customer }: Props) {
    const [opened, { open, close }] = useDisclosure(false);
    const [phone, setPhone] = useState("");
    const [error, setError] = useState<string | null>(null);
    const [loading, setLoading] = useState(false);


    const functions = useFunctions();
    functions.region = "europe-west1";

    const createContractUserKYCLink = httpsCallable<CreateContractLink, BaseResult>(functions, "createContractUserKYCLink");

    // Companies and trusts use the same authorized-signatory KYC flow.
    const isEligible = isEntityKycCustomer(customer);

    if (!isEligible) return null;

    const handleOpen = () => {
        setPhone("");
        setError(null);
        open();
    };

    const handleClose = () => {
        if (loading) return;
        setError(null);
        close();
    };

    const handleSend = async () => {
        if (loading) return;

        if (!isValidKuwaitMobile(phone)) {
            setError("Enter a valid 8-digit Kuwait mobile number (starts with 4, 5, 6 or 9)");
            return;
        }

        setLoading(true);
        setError(null);
        try {
            const user = getAuth().currentUser;
            if (!user) throw new Error("Not logged in");
            const token = await user.getIdToken();

            const res = await createContractUserKYCLink({
                productRef: `customers/${customer.id}`,
                customerID: customer.id,
                token,
                manual: false,
                signingMethod: "paci",
                linkWhatsappNumber: phone,
            });

            if ((res.data as any)?.success === false) {
                throw new Error((res.data as any)?.message ?? "Failed to send link");
            }

            notifications.show({ color: "green", message: `KYC link sent to ${phone}` });
            close();
        } catch (e: any) {
            const msg = e?.message ?? "Something went wrong";
            setError(msg);
            notifications.show({ color: "red", message: msg });
        } finally {
            setLoading(false);
        }
    };

    return (
        <>
            <Button size="xs" variant="light" color="blue" onClick={handleOpen}>
                Update {customer.customerType === "trust" ? "Trust" : "Company"} KYC
            </Button>

            <Modal opened={opened} onClose={handleClose} title={`Send Update ${customer.customerType === "trust" ? "Trust" : "Company"} KYC Link`} centered>
                <Stack>
                    <Text size="sm" c="dimmed">
                        The KYC link will be sent to this WhatsApp number.
                    </Text>
                    <TextInput
                        label="WhatsApp number"
                        placeholder="5XXXXXXX"
                        inputMode="numeric"
                        value={phone}
                        onChange={(e) => {
                            setPhone(cleanInput(e.currentTarget.value));
                            if (error) setError(null);
                        }}
                        error={error}
                        disabled={loading}
                        data-autofocus
                        onKeyDown={(e) => e.key === "Enter" && handleSend()}
                    />
                    <Group justify="flex-end">
                        <Button variant="default" onClick={handleClose} disabled={loading}>
                            Cancel
                        </Button>
                        <Button onClick={handleSend} loading={loading}>
                            Send
                        </Button>
                    </Group>
                </Stack>
            </Modal>
        </>
    );
}